import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Loader2, Check, Bell, X } from "lucide-react";
import { API } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CHANNELS = ["App", "Web", "Kiosk", "SMS"];

const EMPTY_RULE = {
  name: "", enabled: true, reward_unit: "percent", effect_type: "add", value: 5,
  schedule: { start_date: "", end_date: "", ongoing: true, days: [...DAYS], limit_hours: false, start_hour: "09:00", end_hour: "17:00" },
  applies_to: "entire_order", applies_to_value: "",
  apply_to_tiers: "all", selected_tiers: [],
  channels: ["App", "Web", "Kiosk"], locations: "all", selected_locations: [],
  notify_customers: false,
};

function TypeBadge({ rule }) {
  const v = rule.value || 0;
  const unit = rule.reward_unit === "flat" ? "pts" : "%";
  const prefix = rule.effect_type === "multiply" ? "x" : rule.effect_type === "replace" ? "=" : "+";
  const label = rule.effect_type === "multiply" ? `x${v}` : `${prefix}${v}${unit}`;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-warning/15 text-ink">{label}</span>
      <span className="text-xs text-ink/40 capitalize">{rule.effect_type}</span>
    </span>
  );
}

function ScheduleText({ schedule }) {
  if (!schedule) return <span className="text-xs text-ink/40">Always</span>;
  const s = schedule;
  const parts = [];
  if (s.days && s.days.length < 7) {
    const dayStr = s.days.length <= 2 ? s.days.join(", ") : s.days.length === 5 && !s.days.includes("Sat") && !s.days.includes("Sun") ? "Mon-Fri" : `${s.days.length} days`;
    parts.push(dayStr);
  }
  if (s.limit_hours) parts.push(`${s.start_hour}-${s.end_hour}`);
  if (s.start_date && !s.ongoing) {
    const from = s.start_date.slice(5);
    const to = s.end_date ? s.end_date.slice(5) : "";
    parts.push(to ? `${from} – ${to}` : `from ${from}`);
  } else if (s.ongoing) {
    parts.push("ongoing");
  }
  return <span className="text-xs text-ink/50">{parts.join(" · ") || "Always"}</span>;
}

function ScopeText({ rule }) {
  if (rule.applies_to === "categories") return <span className="text-xs text-ink/60">{rule.applies_to_value || "Categories"}</span>;
  if (rule.applies_to === "dishes") return <span className="text-xs text-ink/60">{rule.applies_to_value || "Dishes"}</span>;
  return <span className="text-xs text-ink/50">Entire order</span>;
}

/* ── Toggle Button Group ── */
function BtnGroup({ options, value, onChange, multi }) {
  const selected = multi ? (value || []) : value;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const active = multi ? selected.includes(o) : selected === o;
        return (
          <button key={o} type="button" onClick={() => {
            if (multi) onChange(active ? selected.filter(x => x !== o) : [...selected, o]);
            else onChange(o);
          }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${active ? "bg-purple-500 text-white border-ink" : "bg-white text-ink/60 border-ink/15 hover:border-ink/30"}`}>
            {multi && active && <Check size={10} className="inline mr-1" />}{o}
          </button>
        );
      })}
    </div>
  );
}

/* ── Rule Editor Modal ── */
function RuleModal({ open, onClose, rule, onSave, saving }) {
  const [form, setForm] = useState({ ...EMPTY_RULE });

  useEffect(() => {
    if (open) setForm(rule ? JSON.parse(JSON.stringify(rule)) : { ...EMPTY_RULE, schedule: { ...EMPTY_RULE.schedule, days: [...DAYS] }, channels: ["App", "Web", "Kiosk"] });
  }, [open, rule]);

  const set = (path, val) => {
    setForm(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const p = path.split(".");
      let r = c;
      for (let i = 0; i < p.length - 1; i++) { if (!r[p[i]]) r[p[i]] = {}; r = r[p[i]]; }
      r[p[p.length - 1]] = val;
      return c;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-ink/5">
          <DialogTitle className="text-xl font-bold text-ink">{rule ? "Edit earning rule" : "New earning rule"}</DialogTitle>
          <p className="text-sm text-ink/40">Extra cashback or fixed points awarded for matching orders</p>
        </DialogHeader>
        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Name</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Double points on Pierogi weekend" className="mt-1 h-10 rounded-xl" data-testid="rule-name" />
          </div>

          {/* Reward Unit */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Reward Unit</Label>
            <div className="mt-1"><BtnGroup options={["percent", "flat"]} value={form.reward_unit} onChange={v => set("reward_unit", v)} /></div>
          </div>

          {/* Effect Type + Value */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Effect Type</Label>
              <div className="mt-1"><BtnGroup options={["add", "multiply", "replace"]} value={form.effect_type} onChange={v => set("effect_type", v)} /></div>
              <p className="text-[10px] text-ink/30 mt-1">
                {form.effect_type === "add" && "Adds to the tier rate. e.g. +5% on top of 10% tier = 15%."}
                {form.effect_type === "multiply" && "Multiplies the base rate. e.g. x2 doubles the points earned."}
                {form.effect_type === "replace" && "Replaces the tier rate entirely with this value."}
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Value</Label>
              <div className="flex items-center mt-1">
                <span className="text-sm text-ink/40 mr-1">{form.effect_type === "multiply" ? "x" : "+"}</span>
                <Input type="number" value={form.value} onChange={e => set("value", parseFloat(e.target.value) || 0)} className="h-10 rounded-xl text-center" data-testid="rule-value" />
                <span className="text-sm text-ink/40 ml-1">{form.reward_unit === "flat" ? "pts" : "%"}</span>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="p-4 bg-surface rounded-xl space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Schedule</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-ink/40 mb-1">Start Date</p>
                <Input type="date" value={form.schedule?.start_date || ""} onChange={e => set("schedule.start_date", e.target.value)} className="h-9 rounded-lg text-xs" />
              </div>
              <div>
                <p className="text-[10px] text-ink/40 mb-1">End Date</p>
                <Input type="date" value={form.schedule?.end_date || ""} onChange={e => set("schedule.end_date", e.target.value)} disabled={form.schedule?.ongoing} className="h-9 rounded-lg text-xs" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.schedule?.ongoing !== false} onChange={e => set("schedule.ongoing", e.target.checked)} className="rounded" />
              <span className="text-xs text-ink/60">Ongoing (no end date)</span>
            </label>

            <div>
              <p className="text-[10px] text-ink/40 mb-1">Days of the Week</p>
              <BtnGroup options={DAYS} value={form.schedule?.days || DAYS} onChange={v => set("schedule.days", v)} multi />
              <p className="text-[10px] text-ink/30 mt-1">{(form.schedule?.days || []).length === 7 ? "Runs every day" : `${(form.schedule?.days || []).length} days selected`}</p>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-ink/60 font-medium">Limit to Specific Hours</span>
              <Switch checked={form.schedule?.limit_hours || false} onCheckedChange={v => set("schedule.limit_hours", v)} />
            </div>
            {form.schedule?.limit_hours && (
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[10px] text-ink/40 mb-1">From</p><Input type="time" value={form.schedule?.start_hour || "09:00"} onChange={e => set("schedule.start_hour", e.target.value)} className="h-9 rounded-lg text-xs" /></div>
                <div><p className="text-[10px] text-ink/40 mb-1">To</p><Input type="time" value={form.schedule?.end_hour || "17:00"} onChange={e => set("schedule.end_hour", e.target.value)} className="h-9 rounded-lg text-xs" /></div>
              </div>
            )}
          </div>

          {/* Applies To */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Applies To</Label>
            <div className="mt-1"><BtnGroup options={["entire_order", "categories", "dishes"]} value={form.applies_to} onChange={v => set("applies_to", v)} /></div>
            {form.applies_to !== "entire_order" && (
              <Input value={form.applies_to_value || ""} onChange={e => set("applies_to_value", e.target.value)} placeholder={form.applies_to === "categories" ? "e.g. Pierogi, Drinks" : "e.g. Margherita, Latte"} className="mt-2 h-9 rounded-lg text-xs" />
            )}
          </div>

          {/* Tiers */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Apply to Tiers</Label>
            <div className="mt-1"><BtnGroup options={["all", "selected"]} value={form.apply_to_tiers} onChange={v => set("apply_to_tiers", v)} /></div>
            <p className="text-[10px] text-ink/30 mt-1">By default the rule works for all customers. Restrict to selected tiers if you want.</p>
            {form.apply_to_tiers === "selected" && (
              <Input value={(form.selected_tiers || []).join(", ")} onChange={e => set("selected_tiers", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="e.g. Gold, VIP" className="mt-2 h-9 rounded-lg text-xs" />
            )}
          </div>

          {/* Channels */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Channels</Label>
            <div className="mt-1"><BtnGroup options={CHANNELS} value={form.channels || []} onChange={v => set("channels", v)} multi /></div>
          </div>

          {/* Locations */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Locations</Label>
            <div className="mt-1"><BtnGroup options={["all", "selected"]} value={form.locations} onChange={v => set("locations", v)} /></div>
            <p className="text-[10px] text-ink/30 mt-1">By default the rule runs at every location.</p>
            {form.locations === "selected" && (
              <Input value={(form.selected_locations || []).join(", ")} onChange={e => set("selected_locations", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="e.g. SoHo, Camden" className="mt-2 h-9 rounded-lg text-xs" />
            )}
          </div>

          {/* Notify */}
          <div className="flex items-center justify-between p-3 bg-surface rounded-xl">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-ink/40" />
              <div>
                <p className="text-sm font-medium text-ink">Notify customers</p>
                <p className="text-[10px] text-ink/40">Send a push or SMS notification for this rule</p>
              </div>
            </div>
            <Switch checked={form.notify_customers || false} onCheckedChange={v => set("notify_customers", v)} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink/5 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => onClose(false)} className="text-ink/60">Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={saving || !form.name.trim()} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5" data-testid="save-rule-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── MAIN COMPONENT ── */
export default function EarningRulesTab({ vendorId }) {
  const { authHeaders } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/earning-rules/${vendorId}`, { headers: authHeaders() });
      setRules(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [vendorId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editRule?.id) {
        await axios.put(`${API}/earning-rules/${vendorId}/${editRule.id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/earning-rules/${vendorId}`, form, { headers: authHeaders() });
      }
      setModalOpen(false);
      setEditRule(null);
      await load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleToggle = async (rule) => {
    try {
      await axios.patch(`${API}/earning-rules/${vendorId}/${rule.id}/toggle`, {}, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await axios.delete(`${API}/earning-rules/${vendorId}/${rule.id}`, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
  };

  const handleDuplicate = async (rule) => {
    const copy = { ...rule, name: `${rule.name} (copy)`, enabled: false };
    delete copy.id;
    delete copy.created_at;
    delete copy.updated_at;
    delete copy.vendor_id;
    try {
      await axios.post(`${API}/earning-rules/${vendorId}`, copy, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
  };

  const filtered = filter === "all" ? rules : filter === "active" ? rules.filter(r => r.enabled) : rules.filter(r => !r.enabled);
  const activeCount = rules.filter(r => r.enabled).length;
  const inactiveCount = rules.filter(r => !r.enabled).length;

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 size={18} className="animate-spin text-ink/30" /></div>;

  return (
    <div className="space-y-4" data-testid="earning-rules-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5">
          {[
            { key: "all", label: `All · ${rules.length}` },
            { key: "active", label: `Active · ${activeCount}` },
            { key: "inactive", label: `Inactive · ${inactiveCount}` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === f.key ? "bg-purple-500 text-white" : "bg-purple-50 text-ink/50 hover:text-ink/80"}`}
              data-testid={`filter-${f.key}`}>{f.label}</button>
          ))}
        </div>
        <Button size="sm" onClick={() => { setEditRule(null); setModalOpen(true); }} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5 h-8 text-xs" data-testid="add-rule-btn">
          <Plus size={13} /> Add Rule
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
          <p className="text-sm text-ink/40">No earning rules yet. Create your first rule to boost engagement.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-ink/5 text-[10px] font-semibold uppercase tracking-widest text-ink/40">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">When</div>
            <div className="col-span-1">Scope</div>
            <div className="col-span-1">Tiers</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2"></div>
          </div>

          {/* Rows */}
          {filtered.map(r => (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-ink/5 last:border-0 items-center hover:bg-surface/50">
              <div className="col-span-3">
                <p className="text-sm font-medium text-ink truncate">{r.name}</p>
                {r.notify_customers && <p className="text-[10px] text-ink/30 flex items-center gap-1"><Bell size={9} /> push enabled</p>}
              </div>
              <div className="col-span-2"><TypeBadge rule={r} /></div>
              <div className="col-span-2"><ScheduleText schedule={r.schedule} /></div>
              <div className="col-span-1"><ScopeText rule={r} /></div>
              <div className="col-span-1">
                <span className="text-xs text-ink/50">
                  {r.apply_to_tiers === "all" ? "All" : `${(r.selected_tiers || []).length} tiers`}
                </span>
              </div>
              <div className="col-span-1">
                <div className="flex items-center gap-1.5">
                  <Switch checked={r.enabled} onCheckedChange={() => handleToggle(r)} data-testid={`toggle-${r.id}`} />
                  <span className={`text-xs font-medium ${r.enabled ? "text-success" : "text-ink/30"}`}>{r.enabled ? "On" : "Off"}</span>
                </div>
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <button onClick={() => handleDuplicate(r)} className="p-1.5 rounded-lg text-ink/25 hover:text-ink hover:bg-purple-50" title="Duplicate"><Copy size={13} /></button>
                <button onClick={() => { setEditRule(r); setModalOpen(true); }} className="p-1.5 rounded-lg text-ink/25 hover:text-ink hover:bg-purple-50" title="Edit"><Pencil size={13} /></button>
                <button onClick={() => handleDelete(r)} className="p-1.5 rounded-lg text-ink/25 hover:text-danger hover:bg-danger/10" title="Delete"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RuleModal open={modalOpen} onClose={setModalOpen} rule={editRule} onSave={handleSave} saving={saving} />
    </div>
  );
}
